import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { isIaChatRole, isTeamRosterReadRole } from "@bitacorapro/api-types";
import { Shell } from "./components/Shell";
import { AdminCargaMasiva } from "./features/admin/AdminCargaMasiva";
import { AdminOrgDetail } from "./features/admin/AdminOrgDetail";
import { AdminOrgReports } from "./features/admin/AdminOrgReports";
import { AdminOrgs } from "./features/admin/AdminOrgs";
import { AdminOverview } from "./features/admin/AdminOverview";
import { AdminShell } from "./features/admin/AdminShell";
import { RecipeGate } from "./components/RecipeGate";
import { hasContextualProjectNav } from "./lib/nav";
import {
  canImportUnits,
  hasDashboardSurface,
  isFacilityDashboardEnabled,
} from "./lib/capabilities";
import { RequireAuth } from "./lib/auth/RequireAuth";
import { RequirePlatformAdmin } from "./lib/auth/RequirePlatformAdmin";
import { RequireDashboardRole } from "./lib/auth/RequireDashboardRole";
import { SessionExpiryHandler } from "./lib/auth/SessionExpiryHandler";
import { useSession } from "./lib/auth/useSession";
import { useAppReadySignal } from "./lib/snapshot/useAppReady";
import { usePostHogPageview } from "./lib/telemetry/usePostHogPageview";
import { AuthCallback } from "./routes/AuthCallback";
import { CargarUnidades } from "./routes/CargarUnidades";
import { DocumentosProyecto } from "./routes/DocumentosProyecto";
import { Configuracion } from "./routes/Configuracion";
import { ConsultasDelProyectoRoute } from "./routes/ConsultasDelProyectoRoute";
import { AvisosDelProyectoRoute } from "./routes/AvisosDelProyectoRoute";
import { EncuestasDelProyectoRoute } from "./routes/EncuestasDelProyectoRoute";
import { ArchivoProyecto } from "./routes/ArchivoProyecto";
import { DetalleProyecto } from "./routes/DetalleProyecto";
import { DetalleTienda } from "./routes/DetalleTienda";
import { Contratistas } from "./routes/Contratistas";
import { EnlacesCompartidos } from "./routes/EnlacesCompartidos";
import { Equipo } from "./routes/Equipo";
import { AnalisisRoute } from "./routes/AnalisisRoute";
import { IAInsights } from "./routes/IAInsights";
import { Login } from "./routes/Login";
import { DetalleObservacionPostventa } from "./routes/DetalleObservacionPostventa";
import { ObservacionDetailRoute } from "./routes/ObservacionDetailRoute";
import { ObservacionesDelProyectoRoute } from "./routes/ObservacionesDelProyectoRoute";
import { ObservacionesRoute } from "./routes/ObservacionesRoute";
import { OverviewRoute } from "./routes/OverviewRoute";
import { Proyectos } from "./routes/Proyectos";
import { Reporte } from "./routes/Reporte";
import { Tiendas } from "./routes/Tiendas";
import { DEV_ROUTES_ENABLED } from "./lib/env/devRoutes";
// Placeholder.tsx is no longer imported but kept for the remaining
// stub screens (#17, #18) until they ship.

// Module scope, not component scope — lazy() must be called once. On a
// build where DEV_ROUTES_ENABLED folds to a literal `false` (see
// lib/env/devRoutes.ts), this whole branch and the dynamic import() are
// dead code Rollup drops, so /dev/** never reaches the emitted chunk graph
// (BIT-74, ADR-0076; the exclusion mechanism ADR-0076's Consequences says
// has to be built alongside this route, not inherited from the PWA's
// directory-move-based BIT-60 fix, which doesn't apply to this app's
// centralized react-router-dom tree).
const DevRoutes = DEV_ROUTES_ENABLED ? lazy(() => import("./routes/dev/DevRoutes")) : null;

// Route-level defense-in-depth for /ia (ADR-0139): the Sidebar nav entry
// already hides this route for roles outside IA_CHAT_ROLE_KEYS (the chat
// is role-scoped server-side, so facility_supervisor now reaches it with
// its own stores' facts), but a direct URL should degrade to a redirect,
// not the 403 the underlying POST /ia/chat call would otherwise surface
// as a broken screen.
function RequireIaChat({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!isIaChatRole(session?.organization.role ?? "")) {
    return <Navigate to="/" replace />;
  }
  // POST /ia/chat answers VERTICAL_REQUIRED outside facility: a stale
  // bookmark from a postventa role lands on its home, not a dead chat.
  return (
    <RecipeGate allow={isFacilityDashboardEnabled} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

// Route-level defense-in-depth for /equipo (product decision 2026-09-03,
// amends ADR-0089). The Sidebar nav entry already hides this route for
// roles outside TEAM_ROSTER_READ_ROLE_KEYS, but a stale bookmark should
// land somewhere useful instead of rendering the org's full people
// directory. Redirects to "/", not a terminal no-access screen: a
// store_manager still has a working dashboard, only this one surface is
// out of scope for them.
function RequireTeamRosterRead({ children }: { children: ReactNode }) {
  const session = useSession();
  if (!isTeamRosterReadRole(session?.organization.role ?? "")) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// Route-level defense-in-depth for /contratistas. The nav entry is
// already gated on the recipe having an observation lifecycle
// (ADR-0058: a non-empty observationStatuses vocabulary — never
// recipe.slug, §0.4(6)), because the roster only exists to feed the
// assignment drawer; an org without that lifecycle reaching it by URL or a
// stale ⌘K entry would land on a screen it has no use for (since ADR-0134
// facility has the lifecycle and its «Proveedores» roster). Same posture as
// OverviewRoute: an unresolved recipe draws a skeleton rather than a
// redirect, so a slow /recipes/me never bounces a postventa user off a
// screen they are entitled to (RecipeGate).
export function RequireObservationLifecycle({ children }: { children: ReactNode }) {
  return (
    <RecipeGate allow={(recipe) => recipe.observationStatuses.length > 0} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

// The project-scoped observations list is postventa's working surface
// (panel postventa v2): it reads the postventa feed. Since ADR-0134
// facility ALSO has an observation lifecycle, so RequireObservationLifecycle
// no longer keeps it out — a facility zona is not a project with a list.
// Gated on the same predicate the sidebar uses for the project block
// (`hasContextualProjectNav`); anything else lands on the org-wide list.
export function RequireContextualProjectNav({ children }: { children: ReactNode }) {
  return (
    <RecipeGate allow={hasContextualProjectNav} redirectTo="/observaciones">
      {children}
    </RecipeGate>
  );
}

// Route-level defense-in-depth for /carga-unidades (ADR-0144 §2): the nav
// entry is already gated on canImportUnits; a direct URL from a role or a
// vertical without the importer lands on "/" instead of a screen whose
// every call would 403. An unresolved recipe draws a skeleton, same as
// RequireObservationLifecycle (RecipeGate).
export function RequireUnitImport({ children }: { children: ReactNode }) {
  const session = useSession();
  return (
    <RecipeGate
      allow={(recipe) =>
        canImportUnits(recipe, session?.organization.role, session?.user.platformAdmin)
      }
      redirectTo="/"
    >
      {children}
    </RecipeGate>
  );
}

// Route-level defense-in-depth for every shell screen except the index
// and catch-all: an org whose recipe resolves to no dashboard surface
// (resolveHomeSurface === "none" — residential_rental today) never
// reaches Tiendas / Observaciones / Equipo… by URL. The Shell already
// swaps its body for NoDashboardSurface in that state; this guard keeps
// the route tree honest on its own. Loading draws RecipeGate's neutral
// skeleton, never a redirect, so facility and postventa deep links are
// untouched. Never recipe.slug (ADR-0072).
export function RequireDashboardSurface({ children }: { children: ReactNode }) {
  return (
    <RecipeGate allow={hasDashboardSurface} redirectTo="/">
      {children}
    </RecipeGate>
  );
}

export function App() {
  // Mounted once here, not duplicated into Shell.tsx/AdminShell.tsx
  // (BIT-44). App is the single ancestor of both route trees — Shell and
  // AdminShell are siblings, never simultaneously mounted, so a
  // per-shell instance would have "worked" today, but only by accident:
  // two independent writers to one global attribute with no arbitration
  // is one nested-route refactor away from a stale reading. One writer
  // here removes the question entirely, and covers /login and
  // /auth/callback too even though neither needs it — a no-op for those
  // two, not a liability.
  useAppReadySignal();
  usePostHogPageview();

  return (
    <>
      <SessionExpiryHandler />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* /dev/** — navigable prototype (BIT-74, ADR-0076), structurally
            absent from the emitted bundle unless DEV_ROUTES_ENABLED is true
            at build time (see the DevRoutes const above). Not wrapped in
            RequireAuth: the exclusion is "does not exist in this artifact",
            not "requires a session". */}
        {DevRoutes && (
          <Route
            path="/dev/*"
            element={
              <Suspense fallback={null}>
                <DevRoutes />
              </Suspense>
            }
          />
        )}
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <RequirePlatformAdmin>
                <AdminShell />
              </RequirePlatformAdmin>
            </RequireAuth>
          }
        >
          <Route index element={<AdminOverview />} />
          <Route path="orgs" element={<AdminOrgs />} />
          <Route path="orgs/:id" element={<AdminOrgDetail />} />
          <Route path="orgs/:id/reports" element={<AdminOrgReports />} />
          <Route path="carga-masiva" element={<AdminCargaMasiva />} />
          <Route path="*" element={<AdminOverview />} />
        </Route>
        {/* /reportes/:id — the closed-report reading view (design handoff
            reporte_escritorio §2). A sibling of the Shell group, not a
            child: it opens in its own tab from DetalleTienda's timeline
            and renders its own sticky .rp-top chrome with no sidebar.
            Same auth + role gate as every Shell route. */}
        <Route
          path="reportes/:id"
          element={
            <RequireAuth>
              <RequireDashboardRole>
                <Reporte />
              </RequireDashboardRole>
            </RequireAuth>
          }
        />
        <Route
          element={
            <RequireAuth>
              <RequireDashboardRole>
                <Shell />
              </RequireDashboardRole>
            </RequireAuth>
          }
        >
          <Route index element={<OverviewRoute />} />
          <Route
            element={
              <RequireDashboardSurface>
                <Outlet />
              </RequireDashboardSurface>
            }
          >
            <Route path="tiendas" element={<Tiendas />} />
            <Route path="tiendas/:id" element={<DetalleTienda />} />
            <Route path="proyectos" element={<Proyectos />} />
            <Route path="proyectos/:id" element={<DetalleProyecto />} />
            <Route path="proyectos/:id/archivo" element={<ArchivoProyecto />} />
            {/* ADR-0154 — análisis de cartera con IA. La cartera vive fuera
                de /proyectos/* para que el rail no lea «analisis» como id de
                proyecto; la guardia de rol y vertical está en la ruta. */}
            <Route path="analisis" element={<AnalisisRoute />} />
            <Route path="proyectos/:id/analisis" element={<AnalisisRoute />} />
            {/* Panel postventa v2 (design/claude-design/panel-postventa-v2
                §Navegación): the project's observations and their detail
                live under the project. Gated on the observation lifecycle
                like /contratistas — the list reads the postventa feed, which
                a facility org has no use for. The legacy /observaciones
                routes below keep serving facility and forward postventa
                here (ObservacionesRoute / ObservacionDetailRoute). */}
            <Route
              path="proyectos/:id/observaciones"
              element={
                <RequireContextualProjectNav>
                  <ObservacionesDelProyectoRoute />
                </RequireContextualProjectNav>
              }
            />
            <Route
              path="proyectos/:id/observaciones/:obsId"
              element={
                <RequireObservationLifecycle>
                  <DetalleObservacionPostventa />
                </RequireObservationLifecycle>
              }
            />
            {/* ADR-0145 — la bandeja de consultas del propietario vive bajo
                el proyecto, como las observaciones; `?consulta=<id>` abre el
                hilo (deep link del correo al jefe). Mismo gate que la lista:
                el proyecto como contexto de navegación. Quién puede verla lo
                decide el servidor (403/404). */}
            <Route
              path="proyectos/:id/consultas"
              element={
                <RequireContextualProjectNav>
                  <ConsultasDelProyectoRoute />
                </RequireContextualProjectNav>
              }
            />
            {/* ADR-0152 — avisos y guías de mantención del proyecto, hermana
                de consultas. Quién publica lo decide el servidor (403/404). */}
            <Route
              path="proyectos/:id/avisos"
              element={
                <RequireContextualProjectNav>
                  <AvisosDelProyectoRoute />
                </RequireContextualProjectNav>
              }
            />
            {/* ADR-0153 — encuestas del propietario del proyecto. Quién la
                ve lo decide el servidor (403/404). */}
            <Route
              path="proyectos/:id/encuestas"
              element={
                <RequireContextualProjectNav>
                  <EncuestasDelProyectoRoute />
                </RequireContextualProjectNav>
              }
            />
            <Route path="observaciones" element={<ObservacionesRoute />} />
            <Route path="observaciones/:id" element={<ObservacionDetailRoute />} />
            <Route
              path="contratistas"
              element={
                <RequireObservationLifecycle>
                  <Contratistas />
                </RequireObservationLifecycle>
              }
            />
            <Route path="enlaces" element={<EnlacesCompartidos />} />
            <Route
              path="ia"
              element={
                <RequireIaChat>
                  <IAInsights />
                </RequireIaChat>
              }
            />
            <Route
              path="equipo"
              element={
                <RequireTeamRosterRead>
                  <Equipo />
                </RequireTeamRosterRead>
              }
            />
            <Route
              path="carga-unidades"
              element={
                <RequireUnitImport>
                  <CargarUnidades />
                </RequireUnitImport>
              }
            />
            {/* ADR-0144 §4 — documentos del proyecto: mismo gating que el
                importador de unidades. */}
            <Route
              path="documentos"
              element={
                <RequireUnitImport>
                  <DocumentosProyecto />
                </RequireUnitImport>
              }
            />
            <Route path="configuracion" element={<Configuracion />} />
          </Route>
          <Route path="*" element={<OverviewRoute />} />
        </Route>
      </Routes>
    </>
  );
}
